// ============================================================================
// OMS Returns tab — the shared surface for delivery + customer-success teams.
// Columns: MD #, SO #, Return dispatch status (ADR 2026-10-01 Q13). Admin-only
// authorize button opens the refund modal with QCed-return pre-fill. Filter
// defaults to "picked up but not authorized" — the admin's actionable queue.
// Route: /oms/returns  (registered in src/routes/routes.tsx).
// ============================================================================
import { UndoOutlined } from "@ant-design/icons";
import { Button, Segmented, Space, Table, Tag, Typography } from "antd";
import type { ColumnsType } from "antd/es/table";
import dayjs from "dayjs";
import React, { useCallback, useEffect, useMemo, useState } from "react";
import { usePermissions } from "../../../custom_hooks/permission/permission_hook";
import styles from "../index.module.scss";
import RefundModal, { RefundModalContext } from "./components/refund_modal";
import {
  ReturnRow,
  ReturnRowStatus,
  ReturnsListStatusFilter,
  listReturns,
} from "./returns.api";

const STATUS_LABEL: Record<ReturnRowStatus, { label: string; color: string }> = {
  pending_booking: { label: "Pending booking", color: "blue" },
  booked:          { label: "Booked",          color: "cyan" },
  picked_up:       { label: "Picked up",       color: "geekblue" },
  received:        { label: "Received",        color: "gold" },
  qc_done:         { label: "QC done",         color: "volcano" },
  authorized:      { label: "Authorized",      color: "green" },
};

const OmsReturnsDashboard: React.FC = () => {
  const [filter, setFilter] = useState<ReturnsListStatusFilter>("all_pending");
  const [rows, setRows] = useState<ReturnRow[]>([]);
  const [loading, setLoading] = useState(false);
  const [modalStage, setModalStage] = useState<ReturnRow | null>(null);
  const { hasPermission } = usePermissions();

  // Admin-only authorize per ADR Q13; the permission slug matches the backend seed
  // (organisation/migrations/0026_add_return_refund_perms.py).
  const canAuthorize = hasPermission("refund.row_action");

  const load = useCallback(async () => {
    setLoading(true);
    const resp = await listReturns(filter);
    setRows(resp?.results ?? []);
    setLoading(false);
  }, [filter]);

  useEffect(() => {
    load();
  }, [load]);

  const columns: ColumnsType<ReturnRow> = useMemo(
    () => [
      {
        title: "MD #",
        dataIndex: "md_number",
        key: "md_number",
        render: (val: string | null) => val || <Typography.Text type="secondary">—</Typography.Text>,
      },
      {
        title: "SO #",
        dataIndex: "so_number",
        key: "so_number",
        render: (val, row) => (
          <a href={`/oms/${row.so_id}`} target="_blank" rel="noreferrer">
            {val}
          </a>
        ),
      },
      {
        title: "Return dispatch status",
        dataIndex: "status",
        key: "status",
        render: (status: ReturnRowStatus) => {
          const meta = STATUS_LABEL[status] ?? { label: status, color: "default" };
          return <Tag color={meta.color}>{meta.label}</Tag>;
        },
      },
      {
        title: "Received",
        dataIndex: "created_at",
        key: "created_at",
        render: (v: string | null) => (v ? dayjs(v).format("DD MMM, HH:mm") : "—"),
      },
      {
        title: "Action",
        key: "action",
        render: (_, row) => {
          // Authorize appears only on rows that have been QC'd (or received if ops hasn't gated
          // on QC yet in the backend). Non-authorizers see read-only rows.
          if (row.authorized) {
            return <Typography.Text type="secondary">Done</Typography.Text>;
          }
          if (!canAuthorize) {
            return <Typography.Text type="secondary">—</Typography.Text>;
          }
          const disabled = !row.received;
          return (
            <Button type="primary" size="small" disabled={disabled}
                    onClick={() => setModalStage(row)}>
              Authorize refund
            </Button>
          );
        },
      },
    ],
    [canAuthorize]
  );

  return (
    <div className={styles.container}>
      <div className={styles.header}>
        <UndoOutlined className={styles.headerIcon} />
        <h2 className={styles.title}>OMS — Returns</h2>
        <div className={styles.headerActions}>
          <Segmented
            value={filter}
            onChange={(v) => setFilter(v as ReturnsListStatusFilter)}
            options={[
              { label: "All pending", value: "all_pending" },
              { label: "Receive pending", value: "receive_pending" },
              { label: "QC pending", value: "qc_pending" },
              { label: "Authorize pending", value: "authorize_pending" },
              { label: "Escalated", value: "escalated" },
              { label: "Authorized", value: "authorized" },
              { label: "All", value: "all" },
            ]}
          />
          <Button onClick={load} loading={loading} style={{ marginLeft: 12 }}>
            Refresh
          </Button>
        </div>
      </div>

      <Table<ReturnRow>
        rowKey="stage_id"
        columns={columns}
        dataSource={rows}
        loading={loading}
        pagination={{ pageSize: 50, showSizeChanger: true }}
        locale={{
          emptyText:
            filter === "all_pending"
              ? "No returns waiting on authorization right now."
              : "No returns yet.",
        }}
      />

      {modalStage && (
        <RefundModal
          open={modalStage !== null}
          onClose={() => setModalStage(null)}
          onDone={() => {
            setModalStage(null);
            load();
          }}
          context={RefundModalContext.AUTHORIZE_RETURN}
          stageId={modalStage.stage_id}
          soId={modalStage.so_id}
        />
      )}
    </div>
  );
};

export default OmsReturnsDashboard;
